import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Paper,
  Typography,
  Box,
  makeStyles,
  Button,
  CircularProgress,
} from "@material-ui/core";
import { Search as SearchIcon } from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../../services/api";
import ClienteCustomFieldsForm from "../../../components/ClienteCustomFieldsForm";
import ReceitaCnpjImportDialog from "./ReceitaCnpjImportDialog";

const useStyles = makeStyles((theme) => ({
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    borderBottom: `2px solid ${theme.palette.primary.main}`,
  },
  searchButton: {
    height: "56px",
    minWidth: "56px",
    padding: theme.spacing(1),
  },
}));

const ClienteDadosGerais = ({
  clienteData,
  setClienteData,
  loading,
  fieldRequirements = {}
}) => {
  const classes = useStyles();
  const [usuarios, setUsuarios] = useState([]);
  const [loadingCNPJ, setLoadingCNPJ] = useState(false);
  const [openIframeDialog, setOpenIframeDialog] = useState(false);

  useEffect(() => {
    const fetchUsuarios = async () => {
      try {
        const { data } = await api.get("/users/list");
        setUsuarios(data);
      } catch (error) {
        console.error("Erro ao carregar usuários:", error);
      }
    };
    fetchUsuarios();
  }, []);

  const handleChange = (field) => (event) => {
    const value =
      field === "USUARIO_RESPONSAVEL"
        ? event.target.value
          ? Number(event.target.value)
          : ""
        : event.target.value;

    setClienteData((prevState) => ({ ...prevState, [field]: value }));
  };

  // Função para formatar telefone dinamicamente
  const formatPhone = (value) => {
    if (!value) return "";
    const digits = value.replace(/\D/g, "");
    
    if (digits.length <= 4) {
      return digits;
    } else if (digits.length <= 8) {
      return `${digits.slice(0, 4)}-${digits.slice(4)}`;
    } else {
      return `${digits.slice(0, 5)}-${digits.slice(5, 9)}`;
    }
  };

  const handlePhoneMaskChange = (field) => (event) => {
    const value = event.target.value;
    const digits = value.replace(/\D/g, "").slice(0, 9); // Limita a 9 dígitos
    
    // Salva apenas os dígitos
    setClienteData((prevState) => ({ ...prevState, [field]: digits }));
  };

  const buscarCEP = async (cep) => {
    const cepLimpo = cep.replace(/\D/g, "");
    if (cepLimpo.length !== 8) return;

    try {
      const response = await fetch(
        `https://viacep.com.br/ws/${cepLimpo}/json/`
      );
      const data = await response.json();

      if (!data.erro) {
        setClienteData((prevState) => ({
          ...prevState,
          CEPCLI: cep,
          ENDCLI: data.logradouro || prevState.ENDCLI,
          BAIRROCLI: data.bairro || prevState.BAIRROCLI,
          CIDADECLI: data.localidade || prevState.CIDADECLI,
          ESTADOCLI: data.uf || prevState.ESTADOCLI,
        }));
      }
    } catch (error) {
      console.error("Erro ao buscar CEP:", error);
    }
  };

  // Função para buscar dados do CNPJ
  const buscarDadosCNPJ = async () => {
    const cnpjLimpo = (clienteData.CGCCLI || "").replace(/\D/g, "");
    
    if (cnpjLimpo.length !== 14) {
      toast.warning("Por favor, insira um CNPJ válido com 14 dígitos");
      return;
    }

    setLoadingCNPJ(true);

    try {
      // A consulta sai do backend (BrasilAPI + cache): do navegador esbarrava
      // em CORS e no limite por IP.
      const { data } = await api.get(`/clientes/cnpj/${cnpjLimpo}`);

      // Preenche os dados do formulário
      const dadosAtualizados = {
        NOMECLI: data.razao_social || clienteData.NOMECLI,
        NOMEFANTASIA: data.nome_fantasia || clienteData.NOMEFANTASIA,
        CGCCLI: data.cnpj || clienteData.CGCCLI,
        CEPCLI: (data.cep || "").replace(/\D/g, ""),
        ENDCLI: data.logradouro || clienteData.ENDCLI,
        NUMERO: data.numero || clienteData.NUMERO,
        BAIRROCLI: data.bairro || clienteData.BAIRROCLI,
        CIDADECLI: data.municipio || clienteData.CIDADECLI,
        ESTADOCLI: data.uf || clienteData.ESTADOCLI,
      };

      // Processa e-mail (tenta diferentes formatos)
      const email = data.email || data.EMAIL || data.e_mail || data.E_MAIL || "";
      if (email && email.trim()) {
        dadosAtualizados.E_MAIL = email.trim().toLowerCase();
      }

      // Processa telefones
      if (data.ddd_telefone_1) {
        const telefoneLimpo = data.ddd_telefone_1.replace(/\D/g, "");
        if (telefoneLimpo.length >= 10) {
          dadosAtualizados.DDDCLI = telefoneLimpo.substring(0, 2);
          dadosAtualizados.FONECLI = telefoneLimpo.substring(2);
        }
      }
      
      // Processa telefone 2 (celular) se disponível
      if (data.ddd_telefone_2) {
        const celularLimpo = data.ddd_telefone_2.replace(/\D/g, "");
        if (celularLimpo.length >= 10) {
          dadosAtualizados.DDDCELULAR = celularLimpo.substring(0, 2);
          dadosAtualizados.CELULAR = celularLimpo.substring(2);
        }
      }

      setClienteData((prevState) => ({ ...prevState, ...dadosAtualizados }));
      toast.success("Dados do CNPJ importados com sucesso!");
      
    } catch (error) {
      console.error("Erro ao buscar CNPJ:", error);
      const status = error?.response?.status;

      if (status === 400) {
        // CNPJ digitado errado: a consulta manual não ajuda em nada
        toast.error("CNPJ inválido. Confira os dígitos.", { autoClose: 5000 });
      } else {
        toast.error(
          status === 404
            ? "CNPJ não encontrado na base da Receita. Tente a consulta manual."
            : "Não foi possível buscar os dados automaticamente. Tente usar a consulta manual.",
          { autoClose: 5000 }
        );
        setOpenIframeDialog(true);
      }
    } finally {
      setLoadingCNPJ(false);
    }
  };

  // Aplica o comprovante da Receita importado pelo modal manual. O patch é
  // filtrado pelas chaves já existentes porque o objeto inteiro vai no PUT.
  const handleImportReceita = (fields) => {
    setClienteData((prevState) => {
      const patch = {};
      Object.keys(fields).forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(prevState, key) && fields[key]) {
          patch[key] = fields[key];
        }
      });
      return { ...prevState, ...patch };
    });
    toast.success("Dados do comprovante importados com sucesso!");
  };

  // Função para formatar CNPJ
  const formatCNPJ = (value) => {
    if (!value) return "";
    const digits = value.replace(/\D/g, "");
    
    if (digits.length <= 2) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
    if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
    if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12, 14)}`;
  };

  // Função para formatar CPF
  const formatCPF = (value) => {
    if (!value) return "";
    const digits = value.replace(/\D/g, "");
    
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9, 11)}`;
  };

  const estados = [
    "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA",
    "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN",
    "RS", "RO", "RR", "SC", "SP", "SE", "TO",
  ];

  const isRequired = (fieldName) => {
    if (fieldName === "CGCCLI") {
      return Boolean(fieldRequirements.CGCCLI) && clienteData.PESSOA === "J";
    }

    if (fieldName === "CPFCLI") {
      return Boolean(fieldRequirements.CPFCLI) && clienteData.PESSOA === "F";
    }

    return Boolean(fieldRequirements[fieldName]);
  };

  return (
    <Box>
      {/* Dados Principais */}
      <Paper className={classes.section} elevation={0}>
        <Typography variant="h6" className={classes.sectionTitle}>
          Dados Principais
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Razão Social / Nome"
              fullWidth
              value={clienteData.NOMECLI}
              onChange={handleChange("NOMECLI")}
              disabled={loading}
              required={isRequired("NOMECLI")}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Nome Fantasia"
              fullWidth
              value={clienteData.NOMEFANTASIA}
              onChange={handleChange("NOMEFANTASIA")}
              disabled={loading}
              required={isRequired("NOMEFANTASIA")}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth required={isRequired("PESSOA")}>
              <InputLabel>Tipo de Pessoa</InputLabel>
              <Select
                value={clienteData.PESSOA}
                onChange={handleChange("PESSOA")}
                disabled={loading}
              >
                <MenuItem value="F">Física</MenuItem>
                <MenuItem value="J">Jurídica</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={clienteData.PESSOA === "J" ? 10 : 12} md={clienteData.PESSOA === "J" ? 3 : 4}>
            <TextField
              label={
                clienteData.PESSOA === "J"
                  ? "CNPJ"
                  : "CPF"
              }
              fullWidth
              value={
                clienteData.PESSOA === "J" 
                  ? formatCNPJ(clienteData.CGCCLI)
                  : formatCPF(clienteData.CPFCLI)
              }
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, "");
                const field = clienteData.PESSOA === "J" ? "CGCCLI" : "CPFCLI";
                setClienteData((prevState) => ({ ...prevState, [field]: digits }));
              }}
              disabled={loading}
              required={clienteData.PESSOA === "J" ? isRequired("CGCCLI") : isRequired("CPFCLI")}
              inputProps={{ 
                maxLength: clienteData.PESSOA === "J" ? 18 : 14 
              }}
            />
          </Grid>

          {clienteData.PESSOA === "J" && (
            <Grid item xs={12} sm={2} md={1}>
              <Button
                variant="contained"
                color="primary"
                className={classes.searchButton}
                onClick={buscarDadosCNPJ}
                disabled={loading || loadingCNPJ}
                fullWidth
                title="Buscar dados do CNPJ na Receita Federal"
              >
                {loadingCNPJ ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  <SearchIcon />
                )}
              </Button>
            </Grid>
          )}

          <Grid item xs={12} md={4} style={{ display: "none" }}>
            <TextField
              label="Segmento"
              fullWidth
              value={clienteData.segmento || ""}
              onChange={handleChange("segmento")}
              disabled={loading}
              placeholder="Ex: INDUSTRIAL, COMERCIAL, etc."
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth required={isRequired("STATUS")}>
              <InputLabel>Status</InputLabel>
              <Select
                value={clienteData.STATUS}
                onChange={handleChange("STATUS")}
                disabled={loading}
              >
                <MenuItem value="A">Ativo</MenuItem>
                <MenuItem value="I">Inativo</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth required={isRequired("USUARIO_RESPONSAVEL")}>
              <InputLabel>Usuário Responsável</InputLabel>
              <Select
                value={
                  clienteData.USUARIO_RESPONSAVEL !== null &&
                  clienteData.USUARIO_RESPONSAVEL !== undefined
                    ? String(clienteData.USUARIO_RESPONSAVEL)
                    : ""
                }
                onChange={handleChange("USUARIO_RESPONSAVEL")}
                disabled={loading}
              >
                <MenuItem value="">
                  <em>Nenhum</em>
                </MenuItem>
                {usuarios.map((usuario) => (
                  <MenuItem key={usuario.id} value={String(usuario.id)}>
                    {usuario.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Documentos e Inscrições */}
      <Paper className={classes.section} elevation={0}>
        <Typography variant="h6" className={classes.sectionTitle}>
          Documentos e Inscrições
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              label="Inscrição Estadual"
              fullWidth
              value={clienteData.INSCESTCLI || ""}
              onChange={handleChange("INSCESTCLI")}
              disabled={loading}
              required={isRequired("INSCESTCLI")}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Inscrição Municipal"
              fullWidth
              value={clienteData.insc_municipal || ""}
              onChange={handleChange("insc_municipal")}
              disabled={loading}
              required={isRequired("insc_municipal")}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="SUFRAMA"
              fullWidth
              value={clienteData.suframa || ""}
              onChange={handleChange("suframa")}
              disabled={loading}
              required={isRequired("suframa")}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth required={isRequired("contribuinte_icms")}>
              <InputLabel>Contribuinte ICMS</InputLabel>
              <Select
                value={clienteData.contribuinte_icms || "Não"}
                onChange={handleChange("contribuinte_icms")}
                disabled={loading}
              >
                <MenuItem value="Sim">Sim</MenuItem>
                <MenuItem value="Não">Não</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Contatos */}
      <Paper className={classes.section} elevation={0}>
        <Typography variant="h6" className={classes.sectionTitle}>
          Contatos Principais
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={2}>
            <TextField
              label="DDD"
              fullWidth
              value={clienteData.DDDCLI || ""}
              onChange={handleChange("DDDCLI")}
              disabled={loading}
              inputProps={{ maxLength: 3 }}
              required={isRequired("DDDCLI")}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Telefone"
              fullWidth
              value={formatPhone(clienteData.FONECLI)}
              onChange={handlePhoneMaskChange("FONECLI")}
              disabled={loading}
              placeholder="9999-9999 ou 99999-9999"
              inputProps={{ maxLength: 10 }} // 9 dígitos + 1 hífen
              required={isRequired("FONECLI")}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              label="DDD"
              fullWidth
              value={clienteData.DDDCELULAR || ""}
              onChange={handleChange("DDDCELULAR")}
              disabled={loading}
              inputProps={{ maxLength: 3 }}
              required={isRequired("DDDCELULAR")}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Celular / WhatsApp"
              fullWidth
              value={formatPhone(clienteData.CELULAR)}
              onChange={handlePhoneMaskChange("CELULAR")}
              disabled={loading}
              placeholder="9999-9999 ou 99999-9999"
              inputProps={{ maxLength: 10 }} // 9 dígitos + 1 hífen
              required={isRequired("CELULAR")}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="E-mail"
              fullWidth
              type="email"
              value={clienteData.E_MAIL || ""}
              onChange={handleChange("E_MAIL")}
              disabled={loading}
              required={isRequired("E_MAIL")}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Website"
              fullWidth
              value={clienteData.site || ""}
              onChange={handleChange("site")}
              disabled={loading}
              placeholder="https://www.exemplo.com.br"
              required={isRequired("site")}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Endereço Principal */}
      <Paper className={classes.section} elevation={0}>
        <Typography variant="h6" className={classes.sectionTitle}>
          Endereço Principal
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField
              label="CEP"
              fullWidth
              value={clienteData.CEPCLI || ""}
              onChange={(e) => {
                handleChange("CEPCLI")(e);
                if (e.target.value.replace(/\D/g, "").length === 8) {
                  buscarCEP(e.target.value);
                }
              }}
              disabled={loading}
              required={isRequired("CEPCLI")}
            />
          </Grid>

          <Grid item xs={12} md={7}>
            <TextField
              label="Endereço"
              fullWidth
              value={clienteData.ENDCLI || ""}
              onChange={handleChange("ENDCLI")}
              disabled={loading}
              required={isRequired("ENDCLI")}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              label="Número"
              fullWidth
              value={clienteData.NUMERO || ""}
              onChange={handleChange("NUMERO")}
              disabled={loading}
              required={isRequired("NUMERO")}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Bairro"
              fullWidth
              value={clienteData.BAIRROCLI || ""}
              onChange={handleChange("BAIRROCLI")}
              disabled={loading}
              required={isRequired("BAIRROCLI")}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Cidade"
              fullWidth
              value={clienteData.CIDADECLI || ""}
              onChange={handleChange("CIDADECLI")}
              disabled={loading}
              required={isRequired("CIDADECLI")}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <FormControl fullWidth required={isRequired("ESTADOCLI")}>
              <InputLabel>Estado</InputLabel>
              <Select
                value={clienteData.ESTADOCLI || ""}
                onChange={handleChange("ESTADOCLI")}
                disabled={loading}
              >
                {estados.map((uf) => (
                  <MenuItem key={uf} value={uf}>
                    {uf}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Informações Comerciais */}
      <Paper className={classes.section} elevation={0}>
        <Typography variant="h6" className={classes.sectionTitle}>
          Informações Comerciais
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              label="Limite de Crédito"
              fullWidth
              type="number"
              value={clienteData.LIMITE || 0}
              onChange={handleChange("LIMITE")}
              disabled={loading}
              required={isRequired("LIMITE")}
              InputProps={{
                startAdornment: "R$ ",
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Metas de Vendas"
              fullWidth
              multiline
              rows={2}
              value={clienteData.metas_vendas || ""}
              onChange={handleChange("metas_vendas")}
              disabled={loading}
              required={isRequired("metas_vendas")}
              placeholder="Descreva as metas de vendas..."
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Potencial de Vendas"
              fullWidth
              multiline
              rows={2}
              value={clienteData.potencial_vendas || ""}
              onChange={handleChange("potencial_vendas")}
              disabled={loading}
              required={isRequired("potencial_vendas")}
              placeholder="Descreva o potencial de vendas..."
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Ações de Vendas"
              fullWidth
              multiline
              rows={2}
              value={clienteData.acoes_vendas || ""}
              onChange={handleChange("acoes_vendas")}
              disabled={loading}
              required={isRequired("acoes_vendas")}
              placeholder="Descreva as ações de vendas planejadas..."
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Observações"
              fullWidth
              multiline
              rows={4}
              value={clienteData.OBS || ""}
              onChange={handleChange("OBS")}
              disabled={loading}
              required={isRequired("OBS")}
              placeholder="Observações gerais sobre o cliente..."
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Campos Personalizados */}
      <Paper className={classes.section} elevation={0}>
        <Typography variant="h6" className={classes.sectionTitle}>
          Campos Personalizados
        </Typography>
        <ClienteCustomFieldsForm
          values={clienteData.customFields || {}}
          onChange={(customFields) =>
            setClienteData((prevState) => ({ ...prevState, customFields }))
          }
        />
      </Paper>
      
      {/* Consulta manual no site da Receita Federal */}
      <ReceitaCnpjImportDialog
        open={openIframeDialog}
        onClose={() => setOpenIframeDialog(false)}
        expectedCnpj={clienteData.CGCCLI}
        onImport={handleImportReceita}
      />
    </Box>
  );
};

export default ClienteDadosGerais;
